import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { UsersRound, Plus, Pencil, Loader2, Mail, KeyRound, Copy, Check, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loading, ErrorView } from "@/components/StateViews";
import { StatusDot } from "@/components/Chips";
import { useAuth } from "@/contexts/AuthContext";
import { initials, fmtDate } from "@/lib/format";
import { RolesTab, CustomAccessTab, AuditTab } from "@/components/TeamAccess";

const ROLE_COLORS = { Admin: "bg-navy text-white", "Sales Manager": "bg-loop text-white", "Sales Executive": "bg-sky text-navy" };

function validatePwd(pwd) {
  if (!pwd || pwd.length < 8) return "Password must be at least 8 characters.";
  if (!/[a-zA-Z]/.test(pwd)) return "Password must contain at least one letter.";
  if (!/[0-9]/.test(pwd)) return "Password must contain at least one number.";
  return null;
}

function PasswordManager({ user, onClose }) {
  const [mode, setMode] = useState("menu"); // menu | set | temp
  const [pwd, setPwd] = useState("");
  const [requireChange, setRequireChange] = useState(true);
  const [saving, setSaving] = useState(false);
  const [temp, setTemp] = useState("");
  const [copied, setCopied] = useState(false);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const setPassword = async () => {
    const v = validatePwd(pwd);
    if (v) return toast.error(v);
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/set-password`, { password: pwd, require_change: requireChange });
      toast.success(`Password updated for ${user.name}`);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const generateTemp = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/users/${user.id}/generate-temporary-password`);
      setTemp(data.data.temporary_password);
      setMode("temp");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (mode === "temp") {
    return (
      <div className="space-y-4" data-testid="temp-password-result">
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Copy this temporary password now — it won't be shown again. {user.name} will be required to set a new password on next login.
        </div>
        <div className="flex items-center gap-2 rounded-xl border border-sky bg-mist px-4 py-3">
          <code data-testid="temp-password-value" className="flex-1 font-mono text-base font-semibold text-navy">{temp}</code>
          <button onClick={() => copy(temp)} data-testid="copy-temp-password" className="flex items-center gap-1 rounded-lg border border-sky bg-white px-2.5 py-1.5 text-xs font-medium text-loop hover:bg-sky/30">
            {copied ? <><Check className="h-3.5 w-3.5" />Copied</> : <><Copy className="h-3.5 w-3.5" />Copy</>}
          </button>
        </div>
        <button onClick={onClose} data-testid="temp-password-done" className="w-full rounded-xl bg-loop py-2.5 text-sm font-semibold text-white hover:bg-loophover">Done</button>
      </div>
    );
  }

  if (mode === "set") {
    return (
      <div className="space-y-4" data-testid="set-password-panel">
        <div>
          <label className="mb-1 block text-sm font-medium text-ink/80">New password</label>
          <input data-testid="admin-set-password-input" type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Min 8 chars, 1 letter + 1 number"
            className="h-11 w-full rounded-xl border border-sky px-3.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
        </div>
        <label className="flex items-center gap-2 text-sm text-ink/80">
          <input data-testid="require-change-checkbox" type="checkbox" checked={requireChange} onChange={(e) => setRequireChange(e.target.checked)} className="h-4 w-4 rounded border-sky text-loop" />
          Require {user.name} to change it on next login
        </label>
        <div className="flex gap-2">
          <button onClick={() => setMode("menu")} className="flex-1 rounded-xl border border-sky py-2.5 text-sm font-medium text-ink/70 hover:bg-mist">Back</button>
          <button onClick={setPassword} disabled={saving} data-testid="admin-set-password-save" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-loop py-2.5 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set password"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="password-manager-menu">
      <button onClick={generateTemp} disabled={saving} data-testid="generate-temp-password-button" className="flex w-full items-center gap-3 rounded-xl border border-sky bg-white p-4 text-left hover:border-loop disabled:opacity-60">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-loop/10 text-loop">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}</span>
        <span>
          <span className="block text-sm font-semibold text-navy">Generate temporary password</span>
          <span className="block text-xs text-ink/55">Auto-generate a secure password. User must change it on next login.</span>
        </span>
      </button>
      <button onClick={() => setMode("set")} data-testid="set-specific-password-button" className="flex w-full items-center gap-3 rounded-xl border border-sky bg-white p-4 text-left hover:border-loop">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy/10 text-navy"><KeyRound className="h-5 w-5" /></span>
        <span>
          <span className="block text-sm font-semibold text-navy">Set a specific password</span>
          <span className="block text-xs text-ink/55">Type a password manually and choose whether to force a change.</span>
        </span>
      </button>
    </div>
  );
}

function UserForm({ user, options, managers, onClose, onSaved }) {
  const blank = { name: "", email: "", password: "", role: "Sales Executive", status: "Active", daily_lead_capacity: "", manager_id: "" };
  const [form, setForm] = useState(user ? { ...blank, ...user, manager_id: user.manager_id || "", password: "" } : blank);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!user && (!form.email.trim() || !form.password)) return toast.error("Email and password are required");
    setSaving(true);
    try {
      const payload = { name: form.name, role: form.role, status: form.status, daily_lead_capacity: form.daily_lead_capacity ? Number(form.daily_lead_capacity) : null };
      if (form.password) payload.password = form.password;
      // Only Sales Executives report to a manager; send "" to unassign.
      payload.manager_id = form.role === "Sales Executive" ? (form.manager_id || "") : "";
      if (user?.id) await api.put(`/users/${user.id}`, payload);
      else await api.post("/users", { ...payload, email: form.email });
      toast.success(user ? "User updated" : "User created");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div><label className="mb-1 block text-sm font-medium text-ink/80">Name</label>
        <input data-testid="user-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 w-full rounded-xl border border-sky px-3.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
      </div>
      <div><label className="mb-1 block text-sm font-medium text-ink/80">Email</label>
        <input data-testid="user-email-input" type="email" disabled={!!user} value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11 w-full rounded-xl border border-sky px-3.5 text-sm outline-none focus:border-loop disabled:bg-mist disabled:text-ink/50" />
      </div>
      <div><label className="mb-1 block text-sm font-medium text-ink/80">{user ? "New password (optional)" : "Password"}</label>
        <input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="h-11 w-full rounded-xl border border-sky px-3.5 text-sm outline-none focus:border-loop" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="mb-1 block text-sm font-medium text-ink/80">Role</label>
          <select data-testid="user-role-select" value={form.role} onChange={(e) => set("role", e.target.value)} className="h-11 w-full rounded-xl border border-sky px-3 text-sm outline-none focus:border-loop">
            {(options?.roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div><label className="mb-1 block text-sm font-medium text-ink/80">Status</label>
          <select data-testid="user-status-select" value={form.status} onChange={(e) => set("status", e.target.value)} className="h-11 w-full rounded-xl border border-sky px-3 text-sm outline-none focus:border-loop">
            {(options?.user_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div><label className="mb-1 block text-sm font-medium text-ink/80">Daily lead capacity (optional)</label>
        <input type="number" value={form.daily_lead_capacity || ""} onChange={(e) => set("daily_lead_capacity", e.target.value)} className="h-11 w-full rounded-xl border border-sky px-3.5 text-sm outline-none focus:border-loop" />
      </div>
      {form.role === "Sales Executive" && (
        <div><label className="mb-1 block text-sm font-medium text-ink/80">Reports to (Sales Manager)</label>
          <select data-testid="user-manager-select" value={form.manager_id} onChange={(e) => set("manager_id", e.target.value)} className="h-11 w-full rounded-xl border border-sky px-3 text-sm outline-none focus:border-loop">
            <option value="">— Unassigned —</option>
            {(managers || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <p className="mt-1 text-xs text-ink/45">Used for team-scoped reports & manager accountability.</p>
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <button onClick={onClose} className="flex-1 rounded-xl border border-sky py-2.5 text-sm font-medium text-ink/70 hover:bg-mist">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="user-save-button" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-loop py-2.5 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </button>
      </div>
    </div>
  );
}

export default function Team() {
  const { refreshUsers } = useOutletContext();
  const { options, has } = useAuth();
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [tab, setTab] = useState("members");

  const canManageAccess = has("team.manage_access");
  const TABS = [
    { key: "members", label: "Team Members" },
    ...(canManageAccess ? [
      { key: "roles", label: "Roles & Default Access" },
      { key: "custom", label: "Custom Access" },
      { key: "audit", label: "Access Audit Log" },
    ] : []),
  ];

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await api.get("/users"); setUsers(data.data); }
    catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !users) return <Loading label="Loading team..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const managers = users.filter((u) => u.role === "Sales Manager" && u.status === "Active");
  const nameById = Object.fromEntries(users.map((u) => [u.id, u.name]));

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><UsersRound className="h-7 w-7 text-loop" />Team & Access</h1>
          <p className="text-sm text-ink/60">{users.length} members · Paused/Inactive users skip auto-assignment.</p>
        </div>
        {tab === "members" && has("team.create_user") && (
          <button onClick={() => setDialog("new")} data-testid="new-user-button" className="flex items-center gap-2 rounded-xl bg-loop px-4 py-2.5 text-sm font-semibold text-white hover:bg-loophover"><Plus className="h-4 w-4" />Add Member</button>
        )}
      </div>

      {TABS.length > 1 && (
        <div className="flex flex-wrap gap-1.5 border-b border-sky/60" data-testid="team-tabs">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`team-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "border-loop text-loop" : "border-transparent text-ink/50 hover:text-navy"}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {tab === "roles" && <RolesTab />}
      {tab === "custom" && <CustomAccessTab users={users} />}
      {tab === "audit" && <AuditTab />}

      {tab === "members" && (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-card-${u.id}`} className="rounded-2xl border border-sky/70 bg-white p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-sm font-bold text-white">{initials(u.name)}</span>
                <div>
                  <p className="font-sora font-semibold text-navy">{u.name}</p>
                  <p className="flex items-center gap-1 text-xs text-ink/50"><Mail className="h-3 w-3" />{u.email}</p>
                </div>
              </div>
              <button data-testid={`user-edit-${u.id}`} onClick={() => setDialog(u)} className="rounded-lg border border-sky p-1.5 text-loop hover:bg-sky/30"><Pencil className="h-3.5 w-3.5" /></button>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_COLORS[u.role]}`}>{u.role}</span>
              <StatusDot status={u.status} />
            </div>
            {u.role === "Sales Executive" && (
              <p className="mt-2 text-xs text-ink/50" data-testid={`user-manager-${u.id}`}>
                Reports to: <span className={u.manager_id ? "font-medium text-navy" : "text-amber-600"}>{u.manager_id ? (nameById[u.manager_id] || "(unknown)") : "Unassigned"}</span>
              </p>
            )}
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-ink/40">Joined {fmtDate(u.created_at)}</p>
              <button data-testid={`user-password-${u.id}`} onClick={() => setPwUser(u)} className="flex items-center gap-1.5 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-medium text-navy hover:border-loop hover:text-loop">
                <KeyRound className="h-3.5 w-3.5" />Manage password
              </button>
            </div>
          </div>
        ))}
      </div>
      )}

      <Dialog open={!!pwUser} onOpenChange={(v) => !v && setPwUser(null)}>
        <DialogContent className="border-sky bg-white sm:max-w-md" data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle className="font-sora text-navy">Manage password</DialogTitle>
            <DialogDescription>{pwUser?.name} · {pwUser?.email}</DialogDescription>
          </DialogHeader>
          {pwUser && <PasswordManager user={pwUser} onClose={() => { setPwUser(null); }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="border-sky bg-white sm:max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-sora text-navy">{dialog === "new" ? "Add Team Member" : "Edit Member"}</DialogTitle></DialogHeader>
          <UserForm user={dialog !== "new" ? dialog : null} options={options} managers={managers} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); refreshUsers?.(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
